/* eslint-disable react-hooks/exhaustive-deps */
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import YouTube from "react-youtube";
import { useTV } from "../../../hooks/useTV";
import { useMusicStreams } from "../../../hooks/useMusicStreams";

// ─────────────────────────────────────────────────────────────────────────────
// LiveMusicPlayer — drop-in ambient music module fed by liveChecker's
// /musicLiveStreams node (multi-stream aware, videoId-keyed).
//
//   <LiveMusicPlayer
//     ref={playerRef}                // host can .focus() / .openPicker()
//     variant="floating"             // "floating" | "tile"
//     bottom={90} right={20}         // floating only
//     upRef={exitButtonRef}          // TV: where ArrowUp escapes to
//     pickerReturnRef={musicBtnRef}  // TV: focus target after picker closes
//   />
//
// Host gating: import { useMusicStreams } and hide your selector buttons
// when Object.keys(musicStreams).length === 0 — the module hides itself.
//
// Playback: react-youtube (iframe API). onEnd/onError auto-advance to the
// next live stream instantly; the checker's node prunes ended streams on
// its own cycle as the authoritative layer. Volume pinned to 100 on ready.
// ─────────────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 6; // picker: 3 x 2 per page

// ── Dashboard tile: licensed audio instead of a YouTube video player ──
// Each streamer gave us their music + permission on the condition we credit
// them (cover image + @handle) and only play while they are actually live.
// We host their audio on our CDN and play it through a native <audio>
// element — no video decode, so none of the stutter the embed caused on
// weak webviews. A streamer is only eligible when their channelId appears
// live in musicLiveStreams; we take the cover image from that live entry.
//
// Matching is by channelId (stable — never changes), NOT by name/videoId.
// Order is priority order: the first LIVE source is the default, and
// DisneyMagicalSymphony is first so it wins whenever it is live.
const TILE_AUDIO_MODE = true;
const CDN = "https://cdn.mywaitserver.live/youtube_music_player";
const AUDIO_SOURCES = [
  {
    key: "DisneyMagicalSymphony",
    channelId: "UCcNmT5qGD-Cbf-X0wI_Nu_Q",
    files: [`${CDN}/symphony_1.mp3`, `${CDN}/symphony_2.mp3`],
  },
  {
    key: "CastleOfFlow",
    channelId: "UC31RREkfe0qAU9bIkhT4cVw",
    files: [`${CDN}/flow_1.mp3`, `${CDN}/flow_2.mp3`],
  },
  {
    key: "TheMagicBaton",
    channelId: "UChpX-n7CE6E_PB7fl5et-9A",
    files: [`${CDN}/baton_1.mp3`, `${CDN}/baton_2.mp3`],
  },
  {
    key: "TheKingdomView_Hi",
    channelId: "UCA2UCThNRhG5vttOv3gw7dQ",
    files: [`${CDN}/hi_1.mp3`],
  },
  {
    key: "onceuponatask",
    channelId: "UCs9r3TgPxMyRHFIznZ-bUrw",
    files: [`${CDN}/onceupon_1.mp3`],
  },
];

const LMP_CSS = `
  .lmp-card {
    display: flex; flex-direction: column;
    background: rgba(0, 8, 25, 0.55);
    border: 2px solid rgba(255,255,255,0.4);
    border-radius: 12px; overflow: hidden;
    cursor: pointer; outline: none;
  }
  .lmp-card.lmp-floating {
    position: absolute; z-index: 36;
    width: var(--lmp-card-w);
  }
  .lmp-card.lmp-tile {
    position: relative; width: 100%; height: 100%;
  }
  .lmp-tab {
    width: 100%; text-align: center;
    background: rgba(255,255,255,0.20);
    border: none;
    border-bottom: 2px solid rgba(255,255,255,0.55);
    color: #fff; font-weight: 700; font-family: 'Lato', sans-serif;
    font-size: var(--lmp-tab-font); letter-spacing: 0.6px;
    padding: var(--lmp-tab-pad) 0; cursor: pointer;
    flex-shrink: 0;
  }
  .lmp-tab:focus { outline: 3px solid #3ADF3C; outline-offset: -3px; }
  .lmp-video-wrap {
    position: relative; width: 100%; background: #000; z-index: 1;
  }
  .lmp-floating .lmp-video-wrap { aspect-ratio: 16/9; }
  .lmp-tile .lmp-video-wrap { flex: 1; min-height: 0; }
  .lmp-video-wrap.lmp-collapsed { aspect-ratio: auto; height: 0; flex: 0; overflow: hidden; }
  .lmp-embed, .lmp-embed iframe {
    position: absolute; inset: 0; width: 100%; height: 100%; border: 0;
    pointer-events: none;
  }
  .lmp-embed { opacity: 0.72; }
  .lmp-thumb-fill {
    position: absolute; inset: 0; width: 100%; height: 100%;
    object-fit: cover; display: block;
    background: #0a1428; /* card shows instantly; image fills in when loaded */
  }
  .lmp-yt {
    position: absolute; top: 6px; left: 6px; z-index: 2;
    background: #ff0033; color: #fff; font-weight: 700;
    font-size: var(--lmp-badge-font); border-radius: 4px; padding: 2px 6px;
    letter-spacing: 0.5px; font-family: 'Lato', sans-serif;
  }
  .lmp-live {
    position: absolute; top: 6px; right: 6px; z-index: 2;
    background: #ff0033; color: #fff; font-weight: 700;
    font-size: var(--lmp-badge-font-lg); border-radius: 4px; padding: 2px 8px;
    letter-spacing: 0.6px; font-family: 'Lato', sans-serif;
  }
  .lmp-badges-min {
    display: flex; align-items: center; justify-content: space-between;
    padding: 5px 8px 0;
  }
  .lmp-badges-min .lmp-yt, .lmp-badges-min .lmp-live {
    position: static; top: auto; left: auto; right: auto;
  }
  .lmp-bar {
    display: flex; align-items: center; gap: 8px;
    padding: 6px 10px;
    background: rgba(0, 4, 15, 0.92);
    position: relative; z-index: 2;
    flex-shrink: 0;
  }
  .lmp-bar.lmp-bar-tile {
    flex-direction: column; align-items: stretch; gap: 3px;
  }
  .lmp-caption {
    font-family: 'Lato', sans-serif; font-weight: 700;
    font-size: var(--lmp-caption-font); letter-spacing: 1.2px;
    color: rgba(255,255,255,0.65);
    
  }
  
  .lmp-bar-row {
    display: flex; align-items: center; gap: 8px; min-width: 0;
  }
  .lmp-tile .lmp-label {
    white-space: normal; word-break: break-all;
    line-height: 1.15; font-size: var(--lmp-tile-label);
  }
  .lmp-eq { display: flex; align-items: flex-end; gap: 2px; height: 14px; }
  .lmp-eq span {
    width: 3px; background: #ff0033; border-radius: 1px;
    animation: lmp-eq 0.9s ease-in-out infinite;
  }
  .lmp-eq span:nth-child(1) { height: 60%; animation-delay: 0s; }
  .lmp-eq span:nth-child(2) { height: 100%; animation-delay: 0.2s; }
  .lmp-eq span:nth-child(3) { height: 40%; animation-delay: 0.4s; }
  .lmp-eq span:nth-child(4) { height: 80%; animation-delay: 0.1s; }
  @keyframes lmp-eq { 0%,100% { transform: scaleY(0.5);} 50% { transform: scaleY(1);} }
  .lmp-label {
    font-size: var(--lmp-label-font); color: #ffffff;
    font-weight: 700; letter-spacing: 0.3px; font-family: 'Lato', sans-serif;
    text-shadow: 0 1px 4px rgba(0,0,0,0.8);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }

  /* ── Picker modal ── */
  .lmp-overlay {
    position: fixed; inset: 0; z-index: 200;
    background: rgba(0,0,0,0.75);
    display: flex; align-items: center; justify-content: center;
  }
  .lmp-modal {
    position: relative;
    background: radial-gradient(circle at top, #0d3d8f 0%, #01023f 100%);
    border-radius: 24px; padding: 34px 40px;
    display: flex; flex-direction: column; align-items: center; gap: 20px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.6);
    font-family: 'Lato', sans-serif;
  }
  .lmp-title { color: #fff; font-size: var(--lmp-title-font); font-weight: 600; }
  .lmp-sub { color: rgba(255,255,255,0.75); font-size: var(--lmp-sub-font); margin-top: -8px; }
  .lmp-options {
    display: grid; grid-template-columns: repeat(3, 1fr);
    gap: 12px; width: var(--lmp-grid-w); max-width: 92vw;
  }
  .lmp-option {
    position: relative; display: block; padding: 0; overflow: hidden;
    background: #000; border: 3px solid rgba(255,255,255,0.3);
    border-radius: 12px; cursor: pointer; outline: none;
    font-family: 'Lato', sans-serif;
  }
  .lmp-option:focus { border-color: #3ADF3C; }
  .lmp-option.lmp-current { border-color: rgba(255,255,255,0.25); pointer-events: none; }
  .lmp-option.lmp-current:focus { border-color: #3ADF3C; }
  .lmp-option.lmp-current .lmp-thumb { filter: grayscale(1) brightness(0.4); }
  .lmp-current-overlay {
    position: absolute; inset: 0; z-index: 2;
    display: flex; align-items: center; justify-content: center;
    color: rgba(255,255,255,0.85); font-weight: 700;
    font-size: var(--lmp-cur-font); letter-spacing: 1px; text-align: center;
    text-shadow: 0 2px 8px rgba(0,0,0,0.8);
  }
  .lmp-thumb { width: 100%; aspect-ratio: 16/9; object-fit: cover; display: block; }
  .lmp-opt-bar {
    position: absolute; left: 0; right: 0; bottom: 0;
    background: linear-gradient(to top, rgba(0,0,0,0.94), rgba(0,0,0,0.45) 75%, transparent);
    padding: 14px 8px 6px; display: flex; flex-direction: column; text-align: left;
  }
  .lmp-opt-label { color: #fff; font-weight: 700; font-size: var(--lmp-opt-label); }
  .lmp-opt-sub {
    color: rgba(255,255,255,0.65); font-size: var(--lmp-opt-sub);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .lmp-opt-live {
    position: absolute; top: 6px; left: 6px;
    background: #ff0033; color: #fff; font-weight: 700;
    font-size: var(--lmp-opt-sub); letter-spacing: 0.6px;
    border-radius: 4px; padding: 1px 6px;
  }
  .lmp-pager {
    display: flex; align-items: center; justify-content: center; gap: 16px;
    width: 100%;
  }
  .lmp-page-num {
    color: rgba(255,255,255,0.85); font-weight: 700;
    font-size: var(--lmp-page-font); min-width: var(--lmp-page-minw); text-align: center;
  }
  .lmp-pager-btn {
    background: linear-gradient(135deg, #003580, #007bff);
    color: #fff; font-family: 'Lato', sans-serif; font-weight: 700;
    border: var(--lmp-close-border) solid rgba(255,255,255,0.9);
    border-radius: 12px; padding: var(--lmp-pager-pad);
    font-size: var(--lmp-pager-font);
    cursor: pointer; letter-spacing: 1px; outline: none;
  }
  .lmp-pager-btn:focus { border-color: #3ADF3C; }
  .lmp-close {
    background: rgba(220,38,38,0.85);
    color: #fff; font-family: 'Lato', sans-serif; font-weight: 700;
    border: var(--lmp-close-border) solid rgba(255,255,255,0.9);
    border-radius: 14px; padding: var(--lmp-close-pad); font-size: var(--lmp-close-font);
    cursor: pointer; letter-spacing: 1px; outline: none;
  }
  .lmp-close:focus { border-color: #3ADF3C; }
  .lmp-pager-btn, .lmp-close { flex-shrink: 0; white-space: nowrap; }
`;

const SIZES = {
  tv: {
    cardW: 340, tabFont: 18, tabPad: 8, labelFont: 22, badgeFont: 17,
    titleFont: 40, subFont: 28, gridW: 920, optLabel: 16, optSub: 11,
    curFont: 15, pageFont: 20, pageMinW: 130, pagerFont: 22,
    pagerPad: "12px 30px", closeFont: 30, closePad: "16px 44px", closeBorder: 5,
  },
  display: {
    cardW: 210, tabFont: 18, tabPad: 13, labelFont: 20, badgeFont: 15,
    titleFont: 30, subFont: 20, gridW: 900, optLabel: 15, optSub: 11,
    curFont: 14, pageFont: 18, pageMinW: 120, pagerFont: 20,
    pagerPad: "10px 26px", closeFont: 24, closePad: "12px 34px", closeBorder: 3,
  },
};

function injectStyles() {
  let el = document.getElementById("live-music-player-styles");
  if (!el) {
    el = document.createElement("style");
    el.id = "live-music-player-styles";
    document.head.appendChild(el);
  }
    if (el.textContent !== LMP_CSS) el.textContent = LMP_CSS;
  // Keep this sheet LAST in <head>: PhotoFrame's ".pf-root *" reset ties
  // our selectors on specificity, so source order decides. If a dash
  // injected us before Memories mounted, PhotoFrame's sheet lands later
  // and strips our padding — re-append so we always win the tie.
  if (document.head.lastElementChild !== el) document.head.appendChild(el);
}

// Per-mode sizes delivered as inline CSS variables on the component roots —
// always correct for the CURRENT render, immune to injection-timing races.
function sizeVars(isTv, variant) {
  const P = isTv ? SIZES.tv : SIZES.display;
  // Tile (Dashboard cell) is a much smaller canvas than the floating card,
  // so its badges/labels get their own sizes per device.
  const tile = variant === "tile";
  const badge = tile ? (isTv ? 12 : 14) : P.badgeFont;
  return {
    "--lmp-caption-font": `${tile ? (isTv ? 10 : 12) : 11}px`,
    "--lmp-tile-label": `${tile ? (isTv ? 15 : 16) : P.labelFont}px`,
    "--lmp-card-w": `${P.cardW}px`,
    "--lmp-tab-font": `${P.tabFont}px`,
    "--lmp-tab-pad": `${P.tabPad}px`,
    "--lmp-label-font": `${P.labelFont}px`,
    "--lmp-badge-font": `${badge}px`,
    "--lmp-badge-font-lg": `${badge + 1}px`,
    "--lmp-title-font": `${P.titleFont}px`,
    "--lmp-sub-font": `${P.subFont}px`,
    "--lmp-grid-w": `${P.gridW}px`,
    "--lmp-opt-label": `${P.optLabel}px`,
    "--lmp-opt-sub": `${P.optSub}px`,
    "--lmp-cur-font": `${P.curFont}px`,
    "--lmp-page-font": `${P.pageFont}px`,
    "--lmp-page-minw": `${P.pageMinW}px`,
    "--lmp-pager-font": `${P.pagerFont}px`,
    "--lmp-pager-pad": P.pagerPad,
    "--lmp-close-font": `${P.closeFont}px`,
    "--lmp-close-pad": P.closePad,
    "--lmp-close-border": `${P.closeBorder}px`,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// TileAudioPlayer — licensed-CDN-audio player for BOTH hosts: the Dashboard
// tile (variant="tile") and the Memories floating card (variant="floating").
// Cover image + badges + @handle over a native <audio> engine (seeded random
// start, file chaining, stall recovery, watchdog). Floating adds the
// MINIMIZE tab and a picker modal of the currently-live licensed streamers.
// Same imperative API as the YT player, so host wiring is unchanged.
// ═══════════════════════════════════════════════════════════════════════════
const TileAudioPlayer = forwardRef(function TileAudioPlayer(
  { variant = "tile", bottom = 20, right = 20, upRef, pickerReturnRef },
  ref,
) {
  const { isTv } = useTV();
  injectStyles();
  const cssVars = sizeVars(isTv, variant);
  const { musicStreams } = useMusicStreams();
  const audioRef = useRef(null);

  // Floating (Memories) chrome state. Starts MINIMIZED (floating only —
  // tile must never collapse or the Dashboard cover image disappears).
  const [minimized, setMinimized] = useState(variant === "floating");
  const [showPicker, setShowPicker] = useState(false);
  const tabRef = useRef(null);
  const optionRefs = useRef([]);
  const closeBtnRef = useRef(null);

  // Which hardcoded sources are LIVE right now, in priority order, each
  // carrying the cover image from its live musicLiveStreams entry.
  const liveById = {};
  for (const s of Object.values(musicStreams || {})) {
    if (s?.channelId) liveById[s.channelId] = s;
  }
  const liveSources = AUDIO_SOURCES.map((src) => {
    const live = liveById[src.channelId];
    return live ? { ...src, thumbnail: live.thumbnail, name: live.name } : null;
  }).filter(Boolean);

  const [currentKey, setCurrentKey] = useState(null);
  const [fileIdx, setFileIdx] = useState(0);
  // Bumping this remounts the <audio> element with a fresh src — the only
  // reliable revival for a dead/failed load (advancing fileIdx is a no-op
  // for single-file streamers since (i+1)%1 === 0).
  const [reloadTick, setReloadTick] = useState(0);
  // Dashboard-shared volume: same key + same default as the park live tile,
  // so the two sources are always equally loud. Display only; TV = 100.
  const [volume, setVolume] = useState(() => {
    if (isTv) return 100;
    const v = parseInt(localStorage.getItem("dashVolume"), 10);
    return Number.isFinite(v) ? Math.min(100, Math.max(0, v)) : 20;
  });
  const [isMuted, setIsMuted] = useState(
    () => !isTv && localStorage.getItem("musicMuted") === "1",
  );
  // Random start offset on first play only, so a fresh load feels "live"
  // (mid-track) rather than always from 0:00.
  const didSeedRef = useRef(false);
  // Watchdog-reload resume (Dashboard tile ONLY — Memories floating card
  // always does the normal seeded-random start). Consumed one-shot below.
  const resumeSeekRef = useRef(null);

  // Pick / re-pick the active streamer: remembered choice if it's live,
  // else the first live source (Symphony wins by list order).
  useEffect(() => {
    if (!liveSources.length) {
      if (currentKey) setCurrentKey(null);
      return;
    }
    const stillLive = liveSources.some((s) => s.key === currentKey);
    if (stillLive) return;
    if (variant === "tile") {
      let r = null;
      try {
        r = JSON.parse(localStorage.getItem("wsMusicResume") || "null");
      } catch (e) {
        r = null;
      }
      if (r && Date.now() - (r.ts || 0) < 2 * 60 * 1000) {
        const src = liveSources.find((s) => s.key === r.streamerKey);
        if (src) {
          localStorage.removeItem("wsMusicResume");
          resumeSeekRef.current = { currentTime: r.currentTime || 0 };
          setCurrentKey(src.key);
          setFileIdx(
            Number.isFinite(r.fileIdx)
              ? Math.max(0, r.fileIdx) % src.files.length
              : 0,
          );
          didSeedRef.current = true;
          return;
        }
      }
      localStorage.removeItem("wsMusicResume");
    }
    const pref = localStorage.getItem("musicStreamerPref");
    const byPref = pref && liveSources.find((s) => s.key === pref);
    const pick = byPref || liveSources[0];
    setCurrentKey(pick.key);
    setFileIdx(0);
    didSeedRef.current = false;
  }, [musicStreams]);

  const current = liveSources.find((s) => s.key === currentKey) || null;

  // Apply volume/mute to the <audio> element + persist (display only).
  useEffect(() => {
    if (!isTv) {
      localStorage.setItem("dashVolume", String(volume));
      localStorage.setItem("musicMuted", isMuted ? "1" : "0");
    }
    const a = audioRef.current;
    if (!a) return;
    try {
      a.volume = Math.min(1, Math.max(0, volume / 100));
      a.muted = isMuted;
    } catch (e) {
      /* element mid-swap */
    }
  }, [volume, isMuted, currentKey, fileIdx, isTv]);

  // On first playable moment, jump to a random point in the first file so it
  // feels like joining a live stream already in progress.
  const handleLoaded = () => {
    const a = audioRef.current;
    if (!a) return;
    const r = resumeSeekRef.current;
    if (r) {
      resumeSeekRef.current = null;
      didSeedRef.current = true;
      // Prefer the boot bridge's LIVE position (it kept playing while the
      // dash hydrated); fall back to the saved blob position.
      const b = window.__wsMusicBridge;
      const target =
        b && isFinite(b.currentTime) && b.currentTime > 0
          ? b.currentTime
          : r.currentTime;
      try {
        if (
          a.duration &&
          isFinite(a.duration) &&
          target > 0 &&
          target < a.duration - 5
        ) {
          a.currentTime = target;
        }
      } catch (e) {
        /* seek not ready */
      }
      return;
    }
    if (didSeedRef.current) return;
    didSeedRef.current = true;
    try {
      if (a.duration && isFinite(a.duration) && a.duration > 30) {
        a.currentTime = Math.random() * (a.duration - 15);
      }
    } catch (e) {
      /* seek not ready */
    }
  };

  // Tile audio is actually PLAYING -> retire the boot bridge (if any) so
  // there's never double audio. Safe no-op when no bridge exists.
  const handlePlaying = () => {
    const b = window.__wsMusicBridge;
    if (b) {
      try {
        b.pause();
        b.src = "";
      } catch (e) {
        /* bridge already dead */
      }
      window.__wsMusicBridge = null;
    }
  };
  // File ended -> advance to this streamer's next file, wrapping (loops the
  // sequence forever). Subsequent files start at 0 (only the first is seeded).
  const handleEnded = () => {
    if (!current) return;
    setFileIdx((i) => (i + 1) % current.files.length);
  };

  // RECOVERY: onEnded only fires on a CLEAN finish. A network hiccup pulling
  // from the CDN, a decode blip, or a stalled buffer leaves the element
  // paused mid-track with no event that advances it — that's the "randomly
  // stopped" case. These handlers resume or move on so audio never dead-ends.
  const recover = (why) => {
    const a = audioRef.current;
    if (!a) return;
    // First try to just resume the same file where it left off.
    const p = a.play();
    if (p && typeof p.catch === "function") {
      p.catch(() => {
        // Couldn't resume — advance to the next file (fresh src remounts it).
        if (current) setFileIdx((i) => (i + 1) % current.files.length);
      });
    }
  };
  const handleError = () => recover("error");
  const handleStalled = () => recover("stalled");
  // Watchdog: if the element is paused but shouldn't be (not muted-by-user
  // intent, has a source), nudge it back to playing every 15s.
  useEffect(() => {
    const id = setInterval(() => {
      const a = audioRef.current;
      if (!a) return;
      if (a.paused && !a.ended) {
        // networkState 2 = actively loading — a slow-but-alive fetch.
        // Remounting mid-fetch would restart it (the "overzealous" case),
        // so only declare dead when there's a real error or a src-less
        // element that is NOT loading.
        const fetching = a.networkState === 2;
        if (a.error || (!a.currentSrc && !fetching)) {
          setReloadTick((t) => t + 1);
          return;
        }
        const p = a.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      }
    }, 15000);
    return () => clearInterval(id);
  }, []);
  // Resume beat (tile ONLY): snapshot what's playing every 5s so a watchdog
  // reload picks up exactly here. Blobs older than 2 min are ignored on
  // boot, so normal power-offs never trigger a resume.
  useEffect(() => {
    if (variant !== "tile") return;
    const id = setInterval(() => {
      const a = audioRef.current;
      if (!a || !currentKey || a.paused) return;
      try {
        localStorage.setItem(
          "wsMusicResume",
          JSON.stringify({
            streamerKey: currentKey,
            fileIdx,
            currentTime: a.currentTime || 0,
            src: a.currentSrc || "",
            ts: Date.now(),
          }),
        );
      } catch (e) {
        /* storage write failed — skip beat */
      }
    }, 5000);
    return () => clearInterval(id);
  }, [variant, currentKey, fileIdx]);

  const goToStreamer = (key) => {
    if (key === currentKey) return;
    localStorage.setItem("musicStreamerPref", key);
    setCurrentKey(key);
    setFileIdx(0);
    didSeedRef.current = false;
  };

  // Advance to the next LIVE streamer in order (tile tap + host switchRandom).
  const advanceStreamer = () => {
    if (liveSources.length < 2) return null;
    const idx = liveSources.findIndex((s) => s.key === currentKey);
    const next = liveSources[(idx + 1) % liveSources.length];
    goToStreamer(next.key);
    return next;
  };

  useImperativeHandle(
    ref,
    () => ({
      focus: () =>
        variant === "floating"
          ? tabRef.current?.focus?.()
          : audioRef.current?.focus?.(),
      openPicker: () => {
        if (variant === "floating") setShowPicker(true);
      },
      getSound: () => ({ volume, isMuted }),
      setVolume: (v) => {
        const n = Math.min(100, Math.max(0, Number(v) || 0));
        setVolume(n);
        return { volume: n, isMuted };
      },
      volumeUp: () => {
        const v = Math.min(100, volume + 10);
        setVolume(v);
        return { volume: v, isMuted };
      },
      volumeDown: () => {
        const v = Math.max(0, volume - 10);
        setVolume(v);
        return { volume: v, isMuted };
      },
      toggleMute: () => {
        const m = !isMuted;
        setIsMuted(m);
        return { volume, isMuted: m };
      },
      setMuted: (m) => {
        setIsMuted(!!m);
        return { volume, isMuted: !!m };
      },
      switchRandom: () => advanceStreamer(),
    }),
    [volume, isMuted, currentKey, liveSources.length],
  );

  const closePicker = () => {
    setShowPicker(false);
    if (isTv) {
      setTimeout(
        () => (pickerReturnRef?.current || tabRef.current)?.focus(),
        80,
      );
    }
  };
  const selectSource = (key) => {
    goToStreamer(key);
    closePicker();
  };

  // TV: autofocus the first selectable card when the picker opens.
  useEffect(() => {
    if (isTv && showPicker) {
      const t = setTimeout(() => {
        const firstIdx = liveSources.findIndex((s) => s.key !== currentKey);
        optionRefs.current[Math.max(firstIdx, 0)]?.focus();
      }, 80);
      return () => clearTimeout(t);
    }
  }, [isTv, showPicker]);

  if (!current) return null;

  const floatingStyle =
    variant === "floating" ? { ...cssVars, bottom, right } : { ...cssVars };

  return (
    <>
    <div
      className={`lmp-card lmp-${variant}`}
      style={floatingStyle}
      onClick={(e) => {
        e.stopPropagation();
        // Tile (Dashboard): tap advances. Floating (Memories, display):
        // tap also advances. TV floating stays inert (remote-driven).
        if (variant === "tile" || !isTv) advanceStreamer();
      }}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
    >
      {variant === "floating" && (
        <button
          ref={tabRef}
          className="lmp-tab"
          onClick={(e) => {
            e.stopPropagation();
            setMinimized((v) => !v);
          }}
          onKeyDown={(e) => {
            if (!isTv) return;
            if (e.key === "ArrowUp") {
              e.preventDefault();
              e.stopPropagation();
              upRef?.current?.focus();
              return;
            }
            if (["ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              setMinimized((v) => !v);
            }
          }}
          tabIndex={isTv ? 0 : -1}
        >
          {minimized ? "▲ EXPAND" : "▼ MINIMIZE"}
        </button>
      )}

      {/* Audio element lives in here and stays MOUNTED through minimize —
          collapse only zeroes the height, playback never restarts. */}
      <div className={`lmp-video-wrap ${minimized ? "lmp-collapsed" : ""}`}>
        <img
          className="lmp-thumb-fill"
          src={current.thumbnail}
          alt=""
          loading="eager"
          fetchpriority="high"
          decoding="async"
        />
        <audio
          key={`${currentKey}-${fileIdx}-${reloadTick}`}
          ref={audioRef}
          src={current.files[fileIdx]}
          autoPlay
          playsInline
          onLoadedMetadata={handleLoaded}
          onPlaying={handlePlaying}
          onEnded={handleEnded}
          onError={handleError}
          onStalled={handleStalled}
        />
        <div className="lmp-yt">▶ YouTube</div>
        <span className="lmp-live">LIVE</span>
      </div>

      {variant === "floating" && minimized && (
        <div className="lmp-badges-min">
          <div className="lmp-yt">▶ YouTube</div>
          <span className="lmp-live">LIVE</span>
        </div>
      )}

      <div className={`lmp-bar ${variant === "tile" ? "lmp-bar-tile" : ""}`}>
        
        {variant === "tile" && (
          <div className="lmp-caption">LIVE MUSIC STREAMER</div>
        )}
        <div className="lmp-bar-row">
          <div className="lmp-eq"><span /><span /><span /><span /></div>
          <div className="lmp-label">@{current.name}</div>
        </div>
      </div>
    </div>

    {/* ── Licensed streamer picker — single page (≤5 sources) ── */}
    {showPicker && (
      <div
        className="lmp-overlay"
        style={cssVars}
        onClick={(e) => {
          e.stopPropagation();
          closePicker();
        }}
      >
        <div className="lmp-modal" onClick={(e) => e.stopPropagation()}>
          <div className="lmp-title">Choose Your Cover Music</div>
          <div className="lmp-sub">Live streams from YouTube creators</div>
          <div className="lmp-options">
            {liveSources.map((s, i) => {
              const isCurrent = s.key === currentKey;
              return (
                <button
                  key={s.key}
                  ref={(el) => (optionRefs.current[i] = el)}
                  className={`lmp-option ${isCurrent ? "lmp-current" : ""}`}
                  tabIndex={0}
                  onClick={
                    isCurrent
                      ? undefined
                      : (e) => {
                          e.stopPropagation();
                          selectSource(s.key);
                        }
                  }
                  onKeyDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const last = liveSources.length - 1;
                    const move = (delta) => {
                      const j = Math.max(0, Math.min(i + delta, last));
                      optionRefs.current[j]?.focus();
                    };
                    if (e.key === "ArrowRight") move(1);
                    else if (e.key === "ArrowLeft") move(-1);
                    else if (e.key === "ArrowDown") {
                      if (Math.floor(i / 3) === Math.floor(last / 3))
                        closeBtnRef.current?.focus();
                      else move(3);
                    } else if (e.key === "ArrowUp") {
                      if (i >= 3) move(-3);
                    } else if (e.key === "Enter" || e.key === " ") {
                      if (!isCurrent) selectSource(s.key);
                    } else if (e.key === "Escape" || e.key === "Backspace")
                      closePicker();
                  }}
                >
                  <img className="lmp-thumb" src={s.thumbnail} alt="" />
                  <span className="lmp-opt-live">LIVE</span>
                  {isCurrent && (
                    <div className="lmp-current-overlay">
                      CURRENTLY
                      <br />
                      PLAYING
                    </div>
                  )}
                  <div className="lmp-opt-bar">
                    <span className="lmp-opt-label">@{s.name}</span>
                    <span className="lmp-opt-sub">Live cover music</span>
                  </div>
                </button>
              );
            })}
          </div>

          <button
            ref={closeBtnRef}
            className="lmp-close"
            onClick={(e) => {
              e.stopPropagation();
              closePicker();
            }}
            onKeyDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (e.key === "ArrowUp") {
                optionRefs.current[liveSources.length - 1]?.focus();
              } else if (
                e.key === "Enter" ||
                e.key === " " ||
                e.key === "Escape" ||
                e.key === "Backspace"
              ) {
                closePicker();
              }
            }}
            tabIndex={0}
          >
            CLOSE
          </button>
        </div>
      </div>
    )}
    </>
  );
});

// Inner: the original YouTube-embed player (floating / Memories). Its hooks
// always run — no conditional returns before them.
const LiveMusicPlayerYT = forwardRef(function LiveMusicPlayerYT(
  { variant = "floating", bottom = 20, right = 20, upRef, pickerReturnRef },
  ref,
) {
  const { isTv } = useTV();
  injectStyles();
  const cssVars = sizeVars(isTv, variant);
  const { musicStreams } = useMusicStreams();

  const [musicStream, setMusicStream] = useState(null);
  const [minimized, setMinimized] = useState(variant === "floating");
  // TV: always full volume (no on-device controls; the TV remote handles
  // loudness). Display: modest 50 on first-ever load, then remembers
  // whatever the user set via the volume bar.
  const [volume, setVolume] = useState(() => {
    if (isTv) return 100;
    const v = parseInt(localStorage.getItem("musicVolume"), 10);
    return Number.isFinite(v) ? Math.min(100, Math.max(0, v)) : 50;
  });
  const [isMuted, setIsMuted] = useState(
    () => !isTv && localStorage.getItem("musicMuted") === "1",
  );
  const [showPicker, setShowPicker] = useState(false);
  const [page, setPageState] = useState(0);

  const tabRef = useRef(null);
  const optionRefs = useRef([]);
  const nextBtnRef = useRef(null);
  const prevBtnRef = useRef(null);
  const closeBtnRef = useRef(null);
  const playerRef = useRef(null);
  // Streams the embed itself reported dead (onEnd/onError) — filtered out
  // instantly, ahead of the checker's own node pruning.
  const endedIdsRef = useRef(new Set());
  const [endedTick, setEndedTick] = useState(0);

  const liveMusicList = Object.values(musicStreams || {})
    .filter((s) => s?.videoId && !endedIdsRef.current.has(s.videoId))
    .sort(
      (a, b) =>
        (a.name || "").localeCompare(b.name || "") ||
        a.videoId.localeCompare(b.videoId),
    );

  // Host API
  useImperativeHandle(
    ref,
    () => ({
      focus: () => tabRef.current?.focus(),
      openPicker: () => setShowPicker(true),
      getSound: () => ({ volume, isMuted }),
      setVolume: (v) => {
        const n = Math.min(100, Math.max(0, Number(v) || 0));
        setVolume(n);
        return { volume: n, isMuted };
      },
      volumeUp: () => {
        const v = Math.min(100, volume + 10);
        setVolume(v);
        return { volume: v, isMuted };
      },
      volumeDown: () => {
        const v = Math.max(0, volume - 10);
        setVolume(v);
        return { volume: v, isMuted };
      },
      toggleMute: () => {
        const m = !isMuted;
        setIsMuted(m);
        return { volume, isMuted: m };
      },
      setMuted: (m) => {
        setIsMuted(!!m);
        return { volume, isMuted: !!m };
      },
      // Advance to the NEXT live stream in order (Dashboard "change music"
      // button) — wraps around, matching the display tile's tap behavior.
      switchRandom: () => {
        if (liveMusicList.length < 2) return null;
        const idx = liveMusicList.findIndex(
          (s) => s.videoId === musicStream?.videoId,
        );
        const pick = liveMusicList[(idx + 1) % liveMusicList.length];
        localStorage.setItem("musicStreamerPref", pick.name);
        setMusicStream(pick);
        return pick;
      },
    }),
    [volume, isMuted, musicStream, musicStreams],
  );

  // Apply volume/mute to the live player and persist the choice. Also
  // kick playback on stream switches: loadVideoById on LIVE streams can
  // land UNSTARTED (infinite spinner) instead of auto-playing, especially
  // after mute/unmute API traffic — playVideo() unwedges it.
  useEffect(() => {
    if (!isTv) {
      localStorage.setItem("musicVolume", String(volume));
      localStorage.setItem("musicMuted", isMuted ? "1" : "0");
    }
    // internalPlayer commands are PROMISES — when the player has just been
    // remounted (key change) they reject asynchronously on the dead iframe,
    // which a plain try/catch can't see. Swallow both flavors.
    const safe = (fn) => {
      try {
        const r = fn();
        if (r && typeof r.catch === "function") r.catch(() => {});
      } catch (e) {
        /* sync throw — player mid-swap */
      }
    };
    const apply = () => {
      const p = playerRef.current?.internalPlayer;
      if (!p) return;
      safe(() => p.setVolume(volume));
      safe(() => (isMuted ? p.mute() : p.unMute()));
    };
    apply();
    const t = setTimeout(apply, 700);
    return () => clearTimeout(t);
  }, [volume, isMuted, musicStream?.videoId]);

  // Prune the ended set when the node itself drops those ids.
  useEffect(() => {
    for (const id of [...endedIdsRef.current]) {
      if (!musicStreams?.[id]) endedIdsRef.current.delete(id);
    }
  }, [musicStreams]);

  // Selection reconciliation: keep valid, prefer the persisted channel,
  // else first live; nothing live -> null (module hides).
  useEffect(() => {
    if (!liveMusicList.length) {
      if (musicStream) setMusicStream(null);
      return;
    }
    if (
      musicStream &&
      liveMusicList.some((s) => s.videoId === musicStream.videoId)
    ) {
      return;
    }
    const pref = localStorage.getItem("musicStreamerPref");
    const byPref = pref && liveMusicList.find((s) => s.name === pref);
    setMusicStream(byPref || liveMusicList[0]);
  }, [musicStreams, endedTick]);

  // Picker opens on the page containing the current stream.
  useEffect(() => {
    if (showPicker) {
      const curIdx = liveMusicList.findIndex(
        (s) => s.videoId === musicStream?.videoId,
      );
      setPageState(curIdx > -1 ? Math.floor(curIdx / PAGE_SIZE) : 0);
    }
  }, [showPicker]);

  // TV: autofocus first selectable card — but never steal from pager/close.
  useEffect(() => {
    if (isTv && showPicker) {
      const t = setTimeout(() => {
        const a = document.activeElement;
        if (
          a === nextBtnRef.current ||
          a === prevBtnRef.current ||
          a === closeBtnRef.current
        ) {
          return;
        }
        const start = page * PAGE_SIZE;
        const visible = liveMusicList.slice(start, start + PAGE_SIZE);
        const firstIdx = visible.findIndex(
          (s) => s.videoId !== musicStream?.videoId,
        );
        optionRefs.current[Math.max(firstIdx, 0)]?.focus();
      }, 80);
      return () => clearTimeout(t);
    }
  }, [isTv, showPicker, page, musicStream?.videoId]);

  // ── react-youtube: pinned volume, instant advance on end/error ──
  const ytOpts = {
    width: "100%",
    height: "100%",
    playerVars: {
      autoplay: 1,
      mute: 1, // ALWAYS mount muted — unmuted autoplay is blocked by the
      // webview on remounts (spinner/HTML5 errors). The apply effect and
      // onReady unmute via the API right after, park-tile style.
      controls: 0,
      modestbranding: 1,
      rel: 0,
      playsinline: 1,
      enablejsapi: 1,
    },
  };

  const reclaimFocus = () => {
    // Embeds steal focus on load; give it back to the host anchor — but
    // only if focus was actually stolen (never off a real button).
    if (!isTv) return;
    setTimeout(() => {
      const a = document.activeElement;
      if (!a || a === document.body || a.tagName === "IFRAME") {
        upRef?.current?.focus();
      }
    }, 200);
  };

  const handleReady = (event) => {
    try {
      event.target.setVolume(volume);
      if (isMuted) event.target.mute();
      else event.target.unMute();
    } catch (e) {
      /* player may already be gone */
    }
    reclaimFocus();
  };

  const markEndedAndAdvance = (deadId) => {
    endedIdsRef.current.add(deadId);
    setEndedTick((t) => t + 1); // re-run reconciliation with the id excluded
  };

  const closePicker = () => {
    setShowPicker(false);
    if (isTv) {
      setTimeout(
        () => (pickerReturnRef?.current || tabRef.current)?.focus(),
        80,
      );
    }
  };

  const selectStream = (s) => {
    localStorage.setItem("musicStreamerPref", s.name);
    setMusicStream(s);
    closePicker();
  };

  if (!musicStream) return null;

  const floatingStyle =
    variant === "floating"
      ? { ...cssVars, bottom, right }
      : { ...cssVars };

  // ── Picker derivations ──
  const pageCount = Math.max(1, Math.ceil(liveMusicList.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageStart = safePage * PAGE_SIZE;
  const visible = liveMusicList.slice(pageStart, pageStart + PAGE_SIZE);
  const curIdxVisible = visible.findIndex(
    (x) => x.videoId === musicStream?.videoId,
  );
  const lastV = visible.length - 1;

  const nextPage = () => {
    setPageState((safePage + 1) % pageCount);
    if (isTv) setTimeout(() => nextBtnRef.current?.focus(), 60);
  };
  const prevPage = () => {
    const target = Math.max(safePage - 1, 0);
    setPageState(target);
    if (isTv)
      setTimeout(() => {
        (target > 0 ? prevBtnRef : nextBtnRef).current?.focus();
      }, 60);
  };
  const focusLastSelectable = () => {
    for (let j = lastV; j >= 0; j--) {
      if (j !== curIdxVisible) {
        optionRefs.current[j]?.focus();
        break;
      }
    }
  };
  const moveTo = (from, delta) => {
    let j = from + delta;
    if (j === curIdxVisible) j += delta > 0 ? 1 : -1;
    j = Math.max(0, Math.min(j, lastV));
    if (j === curIdxVisible) return;
    optionRefs.current[j]?.focus();
  };

  return (
    <>
      <div
        className={`lmp-card lmp-${variant}`}
        style={floatingStyle}
        onClick={(e) => {
          e.stopPropagation();
          // Tile mode (Dashboard): tap advances to the next live stream —
          // same interaction as the park live-stream tile.
          if (variant === "tile" && liveMusicList.length > 1) {
            const idx = liveMusicList.findIndex(
              (s) => s.videoId === musicStream?.videoId,
            );
            const next = liveMusicList[(idx + 1) % liveMusicList.length];
            localStorage.setItem("musicStreamerPref", next.name);
            setMusicStream(next);
          }
        }}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
      >
        {variant !== "tile" && (
        <button
          ref={tabRef}
          className="lmp-tab"
          onClick={(e) => {
            e.stopPropagation();
            setMinimized((v) => !v);
          }}
          onKeyDown={(e) => {
            if (!isTv) return;
            if (e.key === "ArrowUp") {
              e.preventDefault();
              e.stopPropagation();
              upRef?.current?.focus();
              return;
            }
            if (["ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              setMinimized((v) => !v);
            }
          }}
          tabIndex={isTv ? 0 : -1}
        >
          {minimized ? "▲ EXPAND" : "▼ MINIMIZE"}
        </button>
        )}

        {/* Player stays mounted through minimize — audio never restarts. */}
        <div className={`lmp-video-wrap ${minimized ? "lmp-collapsed" : ""}`}>
          <YouTube
            ref={playerRef}
            className="lmp-embed"
            videoId={musicStream.videoId}
            opts={ytOpts}
            onReady={handleReady}
            onPlay={handleReady}
            onEnd={() => markEndedAndAdvance(musicStream.videoId)}
            onError={() => markEndedAndAdvance(musicStream.videoId)}
          />
          <div className="lmp-yt">▶ YouTube</div>
          <span className="lmp-live">LIVE</span>
        </div>

        {minimized && (
          <div className="lmp-badges-min">
            <div className="lmp-yt">▶ YouTube</div>
            <span className="lmp-live">LIVE</span>
          </div>
        )}

        <div
          className={`lmp-bar ${variant === "tile" ? "lmp-bar-tile" : ""}`}
        >
          
          {variant === "tile" && (
            <div className="lmp-caption">LIVE MUSIC STREAMER</div>
          )}
          <div className="lmp-bar-row">
            <div className="lmp-eq">
              <span /><span /><span /><span />
            </div>
            <div className="lmp-label">@{musicStream.name}</div>
          </div>
        </div>
      </div>

      {/* ── Streamer picker (paginated 3x2) ── */}
      {showPicker && (
        <div
          className="lmp-overlay"
          style={cssVars}
          onClick={(e) => {
            e.stopPropagation();
            closePicker();
          }}
        >
          <div className="lmp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="lmp-title">Choose Your Cover Music</div>
            <div className="lmp-sub">Live streams from YouTube creators</div>
            <div className="lmp-options">
              {visible.map((s, i) => {
                const isCurrent = i === curIdxVisible;
                return (
                  <button
                    key={s.videoId}
                    ref={(el) => (optionRefs.current[i] = el)}
                    className={`lmp-option ${isCurrent ? "lmp-current" : ""}`}
                    tabIndex={isCurrent ? -1 : 0}
                    onClick={
                      isCurrent
                        ? undefined
                        : (e) => {
                            e.stopPropagation();
                            selectStream(s);
                          }
                    }
                    onKeyDown={
                      isCurrent
                        ? undefined
                        : (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (e.key === "ArrowRight") moveTo(i, 1);
                            else if (e.key === "ArrowLeft") moveTo(i, -1);
                            else if (e.key === "ArrowDown") {
                              if (i + 3 > lastV) {
                                (pageCount > 1 ? nextBtnRef : closeBtnRef)
                                  .current?.focus();
                              } else moveTo(i, 3);
                            } else if (e.key === "ArrowUp") {
                              if (i - 3 >= 0) moveTo(i, -3);
                            } else if (e.key === "Enter" || e.key === " ") {
                              selectStream(s);
                            } else if (
                              e.key === "Escape" ||
                              e.key === "Backspace"
                            ) {
                              closePicker();
                            }
                          }
                    }
                  >
                    <img className="lmp-thumb" src={s.thumbnail} alt="" />
                    <span className="lmp-opt-live">LIVE</span>
                    {isCurrent && (
                      <div className="lmp-current-overlay">
                        CURRENTLY
                        <br />
                        PLAYING
                      </div>
                    )}
                    <div className="lmp-opt-bar">
                      <span className="lmp-opt-label">@{s.name}</span>
                      <span className="lmp-opt-sub">
                        {s.title || "Live cover music"}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            {pageCount > 1 && (
              <div className="lmp-pager">
                {safePage > 0 && (
                  <button
                    ref={prevBtnRef}
                    className="lmp-pager-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      prevPage();
                    }}
                    onKeyDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (e.key === "ArrowUp") focusLastSelectable();
                      else if (e.key === "ArrowDown")
                        closeBtnRef.current?.focus();
                      else if (e.key === "ArrowRight")
                        nextBtnRef.current?.focus();
                      else if (e.key === "Enter" || e.key === " ") prevPage();
                      else if (e.key === "Escape" || e.key === "Backspace")
                        closePicker();
                    }}
                    tabIndex={0}
                  >
                    ◀ PREV
                  </button>
                )}
                <span className="lmp-page-num">
                  Page {safePage + 1}/{pageCount}
                </span>
                <button
                  ref={nextBtnRef}
                  className="lmp-pager-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    nextPage();
                  }}
                  onKeyDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (e.key === "ArrowUp") focusLastSelectable();
                    else if (e.key === "ArrowDown")
                      closeBtnRef.current?.focus();
                    else if (e.key === "ArrowLeft")
                      prevBtnRef.current?.focus();
                    else if (e.key === "Enter" || e.key === " ") nextPage();
                    else if (e.key === "Escape" || e.key === "Backspace")
                      closePicker();
                  }}
                  tabIndex={0}
                >
                  NEXT ▶
                </button>
              </div>
            )}

            <button
              ref={closeBtnRef}
              className="lmp-close"
              onClick={(e) => {
                e.stopPropagation();
                closePicker();
              }}
              onKeyDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (e.key === "ArrowUp") {
                  if (pageCount > 1) nextBtnRef.current?.focus();
                  else focusLastSelectable();
                } else if (
                  e.key === "Enter" ||
                  e.key === " " ||
                  e.key === "Escape" ||
                  e.key === "Backspace"
                ) {
                  closePicker();
                }
              }}
              tabIndex={0}
            >
              CLOSE
            </button>
          </div>
        </div>
      )}
    </>
  );
});

// Wrapper: choose audio tile vs YouTube player. NO hooks here, so the branch
// is safe — each child owns its own hooks and runs them unconditionally.
const LiveMusicPlayer = forwardRef(function LiveMusicPlayer(props, ref) {
  if (TILE_AUDIO_MODE) {
    return <TileAudioPlayer ref={ref} {...props} />;
  }
  return <LiveMusicPlayerYT ref={ref} {...props} />;
});

export default LiveMusicPlayer;
